/**
 * 客户端认证接口（走新 V2 服务器 /api/v2/auth/*，同域 aisom.cn 改后缀）。
 * 字段/流程沿用旧版（用户名+密码+授权码登录；手机/邮箱+验证码注册），视觉由 LoginPage 重做。
 * 会话存 localStorage（token 由服务器下发）；真实校验在服务器，客户端只判有无会话。
 */

const BASE = '/api/v2/auth';
const SESSION_KEY = 'videoai-pro.session.v1';

export interface AuthResult {
  status: string;
  message?: string | undefined;
  token?: string | undefined;
}

export interface SessionUser {
  username: string;
  token?: string | undefined;
}

export function isAuthenticated(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.localStorage.getItem(SESSION_KEY) !== null;
}

export function currentUser(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    const u = (parsed as Partial<SessionUser>)?.username;
    return typeof u === 'string' ? u : null;
  } catch {
    return null;
  }
}

export function setSession(user: SessionUser): void {
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  } catch {
    /* 隐私模式忽略 */
  }
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    /* 忽略 */
  }
}

async function post(path: string, body: Record<string, unknown>): Promise<AuthResult> {
  const r = await fetch(`${BASE}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const env = (await r.json()) as {
    ok?: boolean;
    data?: AuthResult;
    error?: { message?: string };
  };
  // 服务器统一包络 {ok,data}：解出 data（含 status/message/token）；失败包络转 error
  if (env.ok === true && env.data !== undefined) {
    return env.data;
  }
  return { status: 'error', message: env.error?.message ?? '请求失败' };
}

export const authApi = {
  sendSmsCode: (phone: string): Promise<AuthResult> => post('send-sms-code', { phone }),
  sendEmailCode: (email: string): Promise<AuthResult> => post('send-email-code', { email }),
  selfRegister: (payload: Record<string, unknown>): Promise<AuthResult> => post('self-register', payload),
  selfRegisterEmail: (payload: Record<string, unknown>): Promise<AuthResult> => post('self-register-email', payload),
  async login(username: string, password: string, licenseCode?: string): Promise<AuthResult> {
    const d = await post('login', { username, password, license_code: licenseCode ?? '' });
    if (d.status === 'success') {
      setSession({ username, token: d.token });
    }
    return d;
  },
};
